import { memo, useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext, Controller, Control } from 'react-hook-form';
import { Airport } from '../../types/api';
import { DateTime } from 'luxon';

import {
	DATEPICKER_ERROR_MESSAGE,
	TYPEAHEAD_MESSAGES,
	MULTICITY_CONFIG
} from '../../lib/utils/constants';
import handleInputFocus from '../../lib/utils/handle-input-focus';
import {
	Criteria,
	FLIGHT_ONLY,
	REWARD_FLIGHT,
	PRODUCT_TYPES
} from '../../types/criteria';
import useMediaQuery, { BreakPointToken } from '../../hooks/useMediaQuery';

import {
	ButtonComponent,
	ButtonSize,
	ButtonType,
	ButtonPriority,
	ButtonColour,
	IconPosition
} from '@vaa-component-lib/component.atom.button';
import {
	TypographyComponent,
	TypographySize,
	TypographyVariant,
	TypographyWeight
} from '@vaa-component-lib/component.atom.typography';
import {
	IconTravelTakeOffComponent,
	IconTravelGlobeMarkerComponent,
	IconActionPlusComponent
} from '@vaa-component-lib/component.atom.icons';

import DatePickerFieldComponent from '../date-picker-field/date-picker-field.component';
import TypeaheadComponent from '../typeahead/typeahead.component';

import { readOnly } from '../../components/forms/forms.styles';

import {
	Row,
	RowInner,
	RowHeading,
	RemoveLink,
	AddRowButton
} from './multicity-form.styles';

interface MultiCityFormComponentProps {
	control: Control;
	criteria: Criteria;
}

const MultiCityFormComponent = ({
	control,
	criteria
}: MultiCityFormComponentProps) => {
	const { minRows, maxRows } = MULTICITY_CONFIG;
	const { watch, trigger, unregister } = useFormContext();
	const isMobile = useMediaQuery(`(max-width: ${BreakPointToken.MdMin}px)`);

	const [idCounter, setIdCounter] = useState<number>(minRows);
	const [initialised, setInitialised] = useState(false);
	const [rows, setRows] = useState<
		{ id: number; from?: Airport; to?: Airport; departing?: string[] }[]
	>(Array.from({ length: minRows }, (_, i) => ({ id: i })));

	const prevWatchedFields = useRef<string>();

	const watchFieldKeys = useMemo(
		() => ['origin', 'destination', 'departing'],
		[]
	);
	const watchFieldNames = useMemo(
		() =>
			rows.flatMap((row) => watchFieldKeys.map((key) => `${key}_${row.id}`)),
		[rows, watchFieldKeys]
	);

	const watchedFields = watch(watchFieldNames);

	const syncedRows = useMemo(() => {
		return rows.map((row, i) => {
			const rowsIndex = i * watchFieldKeys.length || 0;

			return {
				...row,
				from: watchedFields[rowsIndex] ?? '',
				to: watchedFields[rowsIndex + 1] ?? '',
				departing: watchedFields[rowsIndex + 2] ?? []
			};
		});
	}, [watchedFields, rows, watchFieldKeys]);

	useEffect(() => {
		if (!initialised) return;

		const watchedFieldsString = JSON.stringify(watchedFields);
		if (prevWatchedFields.current !== watchedFieldsString) {
			prevWatchedFields.current = watchedFieldsString;

			setRows((prevRows) =>
				prevRows.map((row, i) => {
					const rowsIndex = i * watchFieldKeys.length || 0;

					const origin = watchedFields[rowsIndex] ?? '';
					const destination = watchedFields[rowsIndex + 1] ?? '';
					const departing = watchedFields[rowsIndex + 2] ?? undefined;

					return {
						...row,
						from: origin,
						to: destination,
						departing: departing
					};
				})
			);
		}
	}, [watchedFields, initialised]);

	useEffect(() => {
		const typedCriteria = criteria as FLIGHT_ONLY | REWARD_FLIGHT;

		if (!typedCriteria) {
			setRows(
				Array.from({ length: minRows }, (_, i) => ({
					id: i,
					from: undefined,
					to: undefined,
					departing: []
				}))
			);
			setInitialised(true);
			return;
		}

		const initialRows = (typedCriteria.origin || []).map((origin, index) => ({
			id: index,
			from: origin ?? undefined,
			to: typedCriteria.destination?.[index] ?? undefined,
			departing: [typedCriteria.departing?.[index] || []].flat()
		}));

		const extendedRows = [
			...initialRows,
			...Array.from(
				{ length: Math.max(minRows - initialRows.length, 0) },
				(_, i) => ({
					id: initialRows.length + i,
					from: undefined,
					to: undefined,
					departing: []
				})
			)
		];

		setRows(extendedRows);
		setIdCounter(initialRows.length < minRows ? minRows : initialRows.length);
		setInitialised(true);
	}, [criteria, minRows]);

	useEffect(() => {
		setRows(syncedRows);
	}, [syncedRows]);

	const handleAddRow = useCallback(() => {
		setRows((prevRows) => {
			if (prevRows.length < maxRows) {
				const lastRow = prevRows[prevRows.length - 1];
				const newRow = {
					id: idCounter,
					from: lastRow?.to || undefined,
					departing: lastRow?.departing || []
				};
				setIdCounter((prevId) => prevId + 1);
				return [...prevRows, newRow];
			}
			return prevRows;
		});
	}, [idCounter, maxRows]);

	const handleRemoveRow = useCallback(
		(idToRemove: number) => {
			watchFieldKeys.forEach((key) => {
				unregister(`${key}_${idToRemove}`);
			});
			setRows((prevRows) => {
				const updatedRows = prevRows.filter((row) => row.id !== idToRemove);
				return updatedRows.length >= minRows ? updatedRows : prevRows;
			});
		},
		[minRows]
	);

	if (!initialised) return null;

	return (
		<>
			{rows?.map((row, index) => {
				const count = index + 1;
				const rowID = row.id;

				return (
					<Row key={`MultiCityRow-${rowID}`}>
						<RowHeading>
							<TypographyComponent
								variant={TypographyVariant.Body}
								size={TypographySize.Sml}
								weight={TypographyWeight.Medium}
								element="h2">
								Flight {count}
							</TypographyComponent>
							{index >= minRows && (
								<RemoveLink>
									<ButtonComponent
										data-cy="remove-row-button"
										text="Remove"
										type={ButtonType.Link}
										size={ButtonSize.Small}
										colour={ButtonColour.Brand}
										onClick={() => handleRemoveRow(rowID)}
									/>
								</RemoveLink>
							)}
						</RowHeading>
						<RowInner>
							<Controller
								name={`origin_${rowID}`}
								control={control}
								defaultValue={row.from || ''}
								rules={{
									validate: {
										requiredOrigin: (value) => {
											if (!value || !value.airportCode)
												return TYPEAHEAD_MESSAGES.requiredOrigin;
											return true;
										}
									}
								}}
								render={({ field: { ref, ...rest }, fieldState }) => (
									<TypeaheadComponent
										label={`Flying from`}
										placeholder={''}
										popoverTitle={`Flying from`}
										historyTitle={TYPEAHEAD_MESSAGES.historyTitle}
										noResultsLabel={TYPEAHEAD_MESSAGES.noResultsMatch}
										iconChild={<IconTravelTakeOffComponent />}
										id={`flights_from_${rowID}`}
										feedback={fieldState.error?.message}
										hasError={!!fieldState.error}
										switchFocusTo={() =>
											handleInputFocus(`flights_to_${rowID}`)
										}
										{...rest}
									/>
								)}
							/>

							<Controller
								name={`destination_${rowID}`}
								control={control}
								defaultValue={row.to || ''}
								rules={{
									validate: {
										requiredDestination: (value) => {
											if (!value || !value.airportCode)
												return TYPEAHEAD_MESSAGES.requiredDestination;
											return true;
										}
									}
								}}
								render={({ field: { ref, ...rest }, fieldState }) => (
									<TypeaheadComponent
										label={'Flying to'}
										placeholder={TYPEAHEAD_MESSAGES.locationPlaceholder}
										popoverTitle={'Flying to'}
										historyTitle={TYPEAHEAD_MESSAGES.historyTitle}
										noResultsLabel={TYPEAHEAD_MESSAGES.noResultsMatch}
										iconChild={<IconTravelGlobeMarkerComponent />}
										id={`flights_to_${rowID}`}
										feedback={fieldState.error?.message}
										hasError={!!fieldState.error}
										switchFocusTo={() =>
											handleInputFocus(`flights_departing_${rowID}`)
										}
										{...rest}
									/>
								)}
							/>

							<Controller
								name={`departing_${rowID}`}
								control={control}
								defaultValue={row.departing || []}
								rules={{
									validate: {
										requiredDate: (value) => {
											if (!value || value.length === 0)
												return DATEPICKER_ERROR_MESSAGE.noSelection;

											if (value.length !== 1)
												return DATEPICKER_ERROR_MESSAGE.oneWay;

											if (index > 0) {
												const prevDate = watch(
													`departing_${rows[index - 1].id}`
												);
												if (
													prevDate &&
													DateTime.fromISO(value[0]) <
														DateTime.fromISO(prevDate[0])
												) {
													return DATEPICKER_ERROR_MESSAGE.backInTime;
												}
											}

											return true;
										}
									}
								}}
								render={({
									field: { onChange, value, ...rest },
									fieldState
								}) => (
									<div css={[readOnly]}>
										<DatePickerFieldComponent
											label={'When'}
											id={`flights_departing_${rowID}`}
											feedback={fieldState.error?.message}
											hasError={!!fieldState.error}
											bookingType={PRODUCT_TYPES.FLIGHT_ONLY}
											selectionType="single"
											value={value}
											onChange={(newValue) => {
												onChange(newValue);
												trigger(watchFieldNames, { shouldFocus: false });
											}}
											{...rest}
										/>
									</div>
								)}
							/>
						</RowInner>
					</Row>
				);
			})}
			<AddRowButton $isActive={rows.length < maxRows}>
				<ButtonComponent
					data-cy="add-row-button"
					type={ButtonType.Button}
					size={ButtonSize.Medium}
					priority={ButtonPriority.Secondary}
					text={'Add flight'}
					onClick={handleAddRow}
					disabled={rows.length >= maxRows}
					fullWidth={isMobile}
					iconChild={<IconActionPlusComponent />}
					iconPosition={IconPosition.Left}
				/>
			</AddRowButton>
		</>
	);
};

export default memo(MultiCityFormComponent);
